import { Component, effect, EventEmitter, input, OnInit, output, Output, signal, ViewChild } from '@angular/core';
import {
  PatientSessionModel,
  PatientTargetDataModel,
  PatientTargetModel,
  PromptCodeModel,
  TargetType,
} from '../../../core/api/models';
import { Utils } from '../../../core/utils';
import {
  StaffApiService,
  SupervisorApiService,
} from '../../../core/api/services';
import { AlertService } from '../../../shared/services/alert.service';
import { AuthService } from '../../../auth/auth.service';
import { Table, TableRowCollapseEvent, TableRowExpandEvent } from 'primeng/table';
import { targetIsEchoic, targetIsPromptBased } from '../../../shared/utils/utils';
import { LoadingIndicatorComponent } from "../../../shared/components/loading-indicator/loading-indicator.component";
import * as FileSaver from 'file-saver';

@Component({
  selector: 'app-rawdata',
  templateUrl: './rawdata.component.html',
  styleUrl: './rawdata.component.css',
  standalone: false,
})
export class RawDataComponent implements OnInit {
  isLoading: boolean = true;

  targetsData: PatientTargetDataModel[] = [];
  defaultPromptCodes = input.required<PromptCodeModel[] | undefined | null>();
  selectedTarget = input.required<PatientTargetModel>();

  rawDataBeingEdited: PatientTargetDataModel | null = null;
  clonedRawData: {
    [s: number]: PatientTargetDataModel;
  } = {};

  startDate: string = new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().substring(0, 10);
  endDate: string = new Date().toISOString().substring(0, 10);
  showDeleted: boolean = false;
  showAddTargetData: boolean = false;

  targetDurationInMinutes = signal<number>(0);
  fluencyDurationInMinutes = signal<number>(0);
  fluencyCorrectCount = signal<number>(0);
  fluencyIncorrectCount = signal<number>(0);
  wholePartialIntervalHappened: boolean = false;
  selectedData: PatientTargetDataModel[] = [];

  @Output() onRawDataDeleted = new EventEmitter<boolean>();

  
  @ViewChild('dt') table!: Table;

  constructor(
    private staffApiService: StaffApiService,
    private supervisorApiService: SupervisorApiService,
    private alertService: AlertService,
    private authService: AuthService
  ) {
    effect(() => {
      this.isLoading = true;
      if (!this.selectedTarget()?.ID) {
        this.isLoading = false;
        return;
      }
      this.loadData();
    });
  }

  ngOnInit(): void {
    this.onRawDataDeleted.subscribe((deleted) => {      
      this.loadData();
    });
  }

  loadData() {
    //Load raw data
    this.supervisorApiService.GetTargetRawData({
      targetId: this.selectedTarget()?.ID!,
      startDate: this.startDate,
      endDate: this.endDate,
    }).subscribe((response) => {
      this.targetsData = response ?? [];
      if (!this.showDeleted) {
        this.targetsData = this.targetsData.filter((t) => !t.DeletedDate);
      }
      this.isLoading = false;
    });
  }

  filterData() {
    this.loadData();
  }

  addData() {
    this.showAddTargetData = true;
  }

  onCloseAddData(event: any) {
    this.showAddTargetData = false;
    this.loadData();
  }

  hideAddData() {
    this.showAddTargetData = false;
  }

  targetIsPromptBased(targetData: PatientTargetDataModel | undefined) {

    return targetIsPromptBased(this.selectedTarget()!);
  }

  targetIsEchoic(targetData: PatientTargetDataModel | undefined) {
    return targetIsEchoic(this.selectedTarget()!);
  }

  firstColorForEchoic(targetData: PatientTargetDataModel) {
    return this.valueColorForTarget(targetData);
  }

  firstTextForEchoic(targetData: PatientTargetDataModel) {
    return this.valueTextForTarget(targetData);
  }

  secondColorForEchoic(targetData: PatientTargetDataModel) {
    return '#5DADE2';
  }

  secondTextForEchoic(targetData: PatientTargetDataModel) {
    return targetData.TextValue?.split('|')[1] ?? 'Unknown';
  }

  valueTextForTarget(targetData: PatientTargetDataModel) {
    // if type uses prompt codes, use it for the text, otherwise use the int value.
    const target = this.selectedTarget();
    if (this.targetIsPromptBased(targetData)) {
      var promptCodeForTargetValue = target?.PromptCodes?.find(
        (p) => p.ID === targetData.IntValue
      );

      if (promptCodeForTargetValue == null) {
        // look for the prompt code in the prompt codes list
        promptCodeForTargetValue = this.defaultPromptCodes()?.find(
          (p) => p.ID === targetData.IntValue
        );
      }

      return promptCodeForTargetValue?.Text ?? 'Unknown - ' + targetData.TextValue;
    } else {
      if (this.targetIsIntValueOnly(targetData)) {
        return targetData.IntValue?.toString() ?? 'No Value For Target';
      } else if (this.targetIsFrequency(targetData)) {
        return targetData.IntValue?.toString() ?? 'No Value For Target';
      }
      else if (this.targetIsToileting(targetData)) {
        return targetData.IntValue ? this.secondsToFormattedString(targetData.IntValue) : 'No Value For Target';
      } else if (this.targetIsWholePartialInterval(targetData)) {
        return (targetData.IntValue == 0 ? 'Did not happen ' : 'Happened') + ' (current interval is ' + this.secondsToFormattedString(target?.Duration!) + ' seconds)';
      } else if (this.targetIsFluencyRate(targetData)) {
        return this.fluencyRateValueText(targetData);
      } else {
        return targetData.TextValue ?? 'No Value For Target';
      }
    }
  }

  secondsToFormattedString(seconds: number) {
    const minutes = Math.floor((seconds % 3600) / 60);
    const seconds1 = seconds % 60;
    return `${minutes < 10 ? '0' + minutes : minutes}:${seconds1 < 10 ? '0' + seconds1 : seconds1}`;
  }

  fluencyRateValueText(targetData: PatientTargetDataModel) {
    const value = targetData.TextValue ?? 'No Value For Target';
    if (!targetData.Duration || targetData.Duration <= 0) {
      return value;
    }

    return `${value} (${this.secondsToFormattedString(targetData.Duration)})`;
  }

  targetIsIntValueOnly(targetData: PatientTargetDataModel) {
    const target = this.selectedTarget();
    return target?.TargetType === TargetType.Quantity;
  }

  targetIsFrequency(targetData?: PatientTargetDataModel) {
    return this.selectedTarget()?.TargetType === TargetType.Frequency;
  }

  valueColorForTarget(targetData: PatientTargetDataModel) {
    // if type uses prompt codes, use it for the text, otherwise use the int value.
    const target = this.selectedTarget();
    if (this.targetIsPromptBased(targetData)) {
      var promptCodeForTargetValue = target?.PromptCodes?.find(
        (p) => p.ID === targetData.IntValue
      );

      if (promptCodeForTargetValue == null) {
        // look for the prompt code in the prompt codes list
        promptCodeForTargetValue = this.defaultPromptCodes()?.find(
          (p) => p.ID === targetData.IntValue
        );
      }

      return promptCodeForTargetValue?.Color ?? 'black';
    } else {
      return 'black';
    }
  }

  promptOptionsForTarget(targetData: PatientTargetDataModel) {
    const target = this.selectedTarget();
    const promptOptionsForTarget = target?.PromptCodes?.map((p) => {
      return {
        value: p.ID,
        text: p.Text,
      };
    });
    return promptOptionsForTarget;
  }

  extraOptionsForEchoicTarget(targetData: PatientTargetDataModel) {
    const firstPartOfValue = targetData.TextValue?.split('|')[0];
    const extraOptionsForEchoicTarget = [
      {
        value: firstPartOfValue + '|Articulation',
        text: 'Articulation',
      },
      {
        value: firstPartOfValue + '|Volume',
        text: 'Volume',
      },
      {
        value: firstPartOfValue + '|Speed',
        text: 'Speed',
      },
      {
        value: firstPartOfValue + '|Tone',
        text: 'Tone',
      },
      {
        value: firstPartOfValue + '|Rhythm',
        text: 'Rhythm',
      },
    ];
    return extraOptionsForEchoicTarget;
  }



  targetIsToileting(targetData: PatientTargetDataModel | undefined) {
    if (!targetData) return false;
    const target = this.selectedTarget();
    return target?.TargetType === TargetType.Toileting;
  }

  targetIsWholePartialInterval(targetData: PatientTargetDataModel | undefined) {
    if (!targetData) return false;
    const target = this.selectedTarget();
    return target?.TargetType === TargetType.WholePartialInterval;
  }

  targetIsFluencyRate(targetData: PatientTargetDataModel | undefined) {
    if (!targetData) return false;
    const target = this.selectedTarget();
    return target?.TargetType === TargetType.FluencyRate;
  }

  targetIsDuration(targetData: PatientTargetDataModel | undefined) {
    if (!targetData) return false;
    const target = this.selectedTarget();
    return target?.TargetType === TargetType.Duration;
  }

  minutesToSeconds(minutes: number): number {
    return Math.round(minutes * 60);
  }

  secondsToMinutes(seconds: number): number {
    return seconds / 60;
  }

  // format from seconds to hh:mm string
  minutesToFormattedString(minutes: number): string {
      const seconds = this.minutesToSeconds(minutes);
      const minutes1 = Math.floor((seconds % 3600) / 60);
      const seconds1 = seconds % 60;
      return `${minutes1 < 10 ? '0' + minutes1 : minutes1}:${seconds1 < 10 ? '0' + seconds1 : seconds1}`;
  }

  updateSessionDataFluencyRate(sessionData: PatientTargetDataModel) {
    if (!sessionData.TextValue) return;
    const fluencyRate = sessionData.TextValue.split('/');
    sessionData.IntValue = parseInt(fluencyRate[0]);
  }

  parseFluencyRateTextValue(textValue: string | null | undefined) {
    const [correct, incorrect] = (textValue ?? '0/0').split('/').map((value) => Number(value));
    return {
      correct: Number.isFinite(correct) ? correct : 0,
      incorrect: Number.isFinite(incorrect) ? incorrect : 0,
    };
  }


  onRawDataEditInit(
    rawData: PatientTargetDataModel 
  ) {
    console.log('Editing raw data:', rawData);

    this.rawDataBeingEdited = rawData;

    if (this.isPatientTargetDataModel(rawData)) {
      if (this.targetIsFrequency(rawData)) {
        const parsed = parseInt(rawData.TextValue ?? '', 10);
        if (!isNaN(parsed) && (rawData.IntValue == null || rawData.IntValue <= 1)) {
          rawData.IntValue = parsed;
        }
      }
      if (this.targetIsDuration(rawData)) {
        this.targetDurationInMinutes.set(this.secondsToMinutes(rawData.IntValue!));
      }
      if (this.targetIsFluencyRate(rawData)) {
        const fluencyValues = this.parseFluencyRateTextValue(rawData.TextValue);
        this.fluencyCorrectCount.set(fluencyValues.correct);
        this.fluencyIncorrectCount.set(fluencyValues.incorrect);
        this.fluencyDurationInMinutes.set(this.secondsToMinutes(rawData.Duration ?? 0));
      }
      if (this.targetIsWholePartialInterval(rawData)) {
        this.wholePartialIntervalHappened = rawData.IntValue === 0 ? false : true;
      }
    }

    this.clonedRawData[rawData.ID as number] = {
      ...rawData,
    } as PatientTargetDataModel ;
  }

  isPatientTargetDataModel(obj: any): obj is PatientTargetDataModel {
    return 'PatientSessionID' in obj && 'PatientTargetID' in obj;
  }

  onRawDataEditSave(
    rawData: PatientTargetDataModel 
  ) {

    if (this.isPatientTargetDataModel(rawData)) {
      if (this.targetIsDuration(rawData)) {
        const intValueInSeconds = this.minutesToSeconds(this.targetDurationInMinutes());
        const textValue = this.minutesToFormattedString(this.targetDurationInMinutes());
        
        // Update the passed object
        rawData.IntValue = intValueInSeconds;
        rawData.TextValue = textValue;
        
        // Also update the object in the array to ensure it's saved correctly
        const index = this.targetsData.findIndex((t) => t.ID === rawData.ID);
        if (index !== -1) {
          this.targetsData[index].IntValue = intValueInSeconds;
          this.targetsData[index].TextValue = textValue;
        }
      }
      if (this.targetIsWholePartialInterval(rawData)) {
        const intValue = this.wholePartialIntervalHappened ? 1 : 0;
        rawData.IntValue = intValue;
        
        // Also update the object in the array
        const index = this.targetsData.findIndex((t) => t.ID === rawData.ID);
        if (index !== -1) {
          this.targetsData[index].IntValue = intValue;
        }
      }
      if (this.targetIsFluencyRate(rawData)) {
        const correctCount = this.fluencyCorrectCount() ?? 0;
        const incorrectCount = this.fluencyIncorrectCount() ?? 0;
        const durationInSeconds = this.minutesToSeconds(this.fluencyDurationInMinutes() ?? 0);

        rawData.IntValue = correctCount;
        rawData.TextValue = `${correctCount}/${incorrectCount}`;
        rawData.Duration = durationInSeconds;

        const index = this.targetsData.findIndex((t) => t.ID === rawData.ID);
        if (index !== -1) {
          this.targetsData[index].IntValue = rawData.IntValue;
          this.targetsData[index].TextValue = rawData.TextValue;
          this.targetsData[index].Duration = rawData.Duration;
        }
      }
      if (this.targetIsIntValueOnly(rawData)) {
        // For quantity targets, ensure IntValue is set and TextValue is null
        // IntValue is already bound via ngModel, just ensure TextValue is null
        if (rawData.IntValue !== null && rawData.IntValue !== undefined) {
          rawData.TextValue = null;
        }
      }
      if (this.targetIsFrequency(rawData)) {
        rawData.TextValue = rawData.IntValue?.toString() ?? null;
        const index = this.targetsData.findIndex((t) => t.ID === rawData.ID);
        if (index !== -1) {
          this.targetsData[index].IntValue = rawData.IntValue;
          this.targetsData[index].TextValue = rawData.TextValue;
        }
      }
    }

    console.log('Updated raw data:', rawData);
    console.log('IntValue being saved:', rawData.IntValue);
    this.saveRawData(rawData);
  }

  onRawDataDelete(
    rawData: PatientTargetDataModel 
  ) {
    console.log('Deleting raw data:', rawData);
    this.alertService
      .confirm(
        'Delete Data',
        'Are you sure you want to delete this data?'
      )
      .subscribe((result) => {
        if (result) {
          this.deleteRawData(rawData);
        }
      });
  }

  onRawDataRestore(
    rawData: PatientTargetDataModel 
  ) {
    console.log('Restoring raw data:', rawData);
    this.restoreRawData(rawData);
  }

  onRawDataEditCancel(
    rawData: PatientTargetDataModel 
  ) {
    console.log('Cancelled editing raw data:', rawData);

    if (this.isPatientTargetDataModel(rawData)) {
      const index = this.targetsData?.findIndex(
        (t) => t.ID === rawData.ID
      );
      if (index === undefined) {
        console.error('Could not find index for raw data:', rawData);
        return;
      }

      this.targetsData![index] = this.clonedRawData[
        rawData.ID as number
      ] as PatientTargetDataModel;
      delete this.clonedRawData[rawData.ID as number];
    }
   
  }

  saveRawData(newRawData: PatientTargetDataModel ) {
    this.isLoading = true;
    console.log('Saving raw data:', newRawData);
    
    const currentUser = this.authService.userInfo;
    const currentUserID = currentUser?.ID;
    if (!newRawData.UserID && currentUserID) {
      newRawData.UserID = currentUserID;
      newRawData.UserFullName = currentUser?.Fullnane ?? '';
    }
    
    this.staffApiService
      .SavePatientTargetData({
        body: newRawData as PatientTargetDataModel,
        addedManually: true,
      })
      .subscribe(
        (response) => {
          console.log('Saved raw data:', response);
          
          this.alertService.success('Raw data saved successfully');
          this.isLoading = false;
          
          // Reload the data list to get the latest from the server
          this.loadData();
        },
        (error) => {
          console.error('Error saving raw data:', error);
          this.alertService.error(
            'Error saving raw data - ' + error.Message
          );
          this.isLoading = false;
        }
      );
  }

  deleteRawData(
    rawData: PatientTargetDataModel 
  ) {
    this.isLoading = true;
    console.log('Deleting raw data:', rawData);
    if (this.isPatientTargetDataModel(rawData)) {
      this.staffApiService
        .MarkTargetDataAsDeleted({
          patientTargetDataID: rawData.ID,
        })
        .subscribe((response) => {
          console.log('Target data deleted:', response);
          this.isLoading = false;
          this.onRawDataDeleted.emit(true);
        });
    } 
  }

  restoreRawData(
    rawData: PatientTargetDataModel 
  ) {
    this.isLoading = true;
    console.log('Deleting raw data:', rawData);
    if (this.isPatientTargetDataModel(rawData)) {
      this.staffApiService
        .RestoreTargetDataFromDelete({
          patientTargetDataID: rawData.ID,
        })
        .subscribe((response) => {
          console.log('Target data restored:', response);
          this.isLoading = false;
          this.onRawDataDeleted.emit(true);
        });
    } 
  }

  getExportData(): any[] {
    const dataToExport = (this.table?.filteredValue) || this.targetsData;
  
    return dataToExport.map((targetData) => {
        return {
          'Session': this.getSessionInfo(targetData),
          'Recorded Date': this.formatDate(targetData.RecordedDate),
          'Value': this.getDisplayValue(targetData),
          'Created By': targetData.UserFullName ?? '',
          'Modified By': targetData.ModifiedByUser ? this.getUserNameFromViewModel(targetData.ModifiedByUser) : 'N/A',
          'Modified Date': targetData.ModifiedDate ? this.formatDate(targetData.ModifiedDate) : '',
          // Add other fields as needed
        };
    });
  }

  formatDate(date: Date): string {
    return new Date(date).toLocaleString(); // Adjust formatting as needed
  }
  
  getDisplayValue(targetData: any): string {
    if (this.targetIsPromptBased(targetData)) {
      if (this.targetIsEchoic(targetData)) {
        return `${this.firstTextForEchoic(targetData)} | ${this.secondTextForEchoic(targetData)}`;
      } else {
        return this.valueTextForTarget(targetData);
      }
    } else if (this.targetIsToileting(targetData)) {
      const toiletingData = targetData.ToiletingData;
      const flags = [];
      if (toiletingData.UrinationOn) flags.push('Urination On');
      if (toiletingData.UrinationOff) flags.push('Urination Off');
      if (toiletingData.BowelMovementOn) flags.push('BM On');
      if (toiletingData.BowelMovementOff) flags.push('BM Off');
      if (toiletingData.NoVoid) flags.push('No Void');
      return flags.join(', ');
    } else if (this.targetIsWholePartialInterval(targetData)) {
      return this.valueTextForTarget(targetData);
    } else if (this.targetIsFluencyRate(targetData)) {
      return targetData.TextValue;
    } else {
      return this.valueTextForTarget(targetData);
    }
  }
  
  getSessionInfo(targetData: any): string {
    const session = targetData.SessionData;
    if (session) {
      const started = this.formatDate(session.StartedDate);
      const finished = this.formatDate(session.FinishedDate);
      const serviceID = session.ServiceID ? `Service: #${session.ServiceID}` : '';
      return `Session on ${started} - ${finished} ${serviceID}`;
    }
    return '';
  }

  exportExcel() {
    import('xlsx').then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(this.getExportData());
      const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
      const excelBuffer: any = xlsx.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
      });
      this.saveAsExcelFile(excelBuffer, 'table_data');
    });
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    const EXCEL_TYPE =
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const EXCEL_EXTENSION = '.xlsx';
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE,
    });
    FileSaver.saveAs(
      data,
      fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION
    );
  }

  formatManuallyAddedDate(date: string | null | undefined): string {
    if (!date) return '';
    const dateObj = new Date(date);
    return dateObj.toLocaleDateString('en-US', { 
      month: '2-digit', 
      day: '2-digit', 
      year: '2-digit' 
    });
  }

  get isAnyRowEditing(): boolean {
    return Object.keys(this.clonedRawData).length > 0;
  }
  
  getUserNameFromViewModel(user: any): string {
    if (!user) return 'N/A';
    if (user.FirstName || user.LastName) {
      return `${user.FirstName || ''} ${user.LastName || ''}`.trim() || user.UserName || 'N/A';
    }
    return user.UserName || 'N/A';
  }
  
  getUserDisplayName(userID: number | null | undefined, userFullName: string | null | undefined): string {
    // Use UserFullName if available, otherwise return empty string (will fallback to N/A in template)
    return userFullName || '';
  }

}
